import WrappedFieldWrapper from './FieldWrapper.js';
import { InputContent } from './styles/Input.js';
import './styles/Time.js';
import './styles/Select.js';
import './styles/Field.js';
import './styles/Password.js';
import React__default, { useRef, useImperativeHandle, useEffect } from 'react';
import { ThemingContext } from '@doordash/design-language';
import { getPropsToForward } from '@doordash/utilities';
import { getThemeFieldTextStyles } from '../utils.js';
import { FieldSize } from '../constants.js';

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

var numberOfLines = function numberOfLines(_ref) {
  var isMultiline = _ref.isMultiline,
      type = _ref.type;

  if (isMultiline && type === 'text') {
    return Number.isInteger(isMultiline) ? isMultiline : 2;
  }

  return 1;
};

var RawTextFieldSize = FieldSize;
/**
 * https://developers.google.com/web/fundamentals/accessibility/semantics-aria/aria-labels-and-relationships
 * https://html.spec.whatwg.org/multipage/form-control-infrastructure.html
 * https://developers.google.com/web/fundamentals/design-and-ui/input/forms/
 */

var RawTextField = function RawTextField(_ref2) {
  var _ref2$isResizeable = _ref2.isResizeable,
      isResizeable = _ref2$isResizeable === void 0 ? true : _ref2$isResizeable,
      _ref2$size = _ref2.size,
      size = _ref2$size === void 0 ? RawTextFieldSize.Medium : _ref2$size,
      _ref2$isRounded = _ref2.isRounded,
      isRounded = _ref2$isRounded === void 0 ? false : _ref2$isRounded,
      _ref2$__focusBehavior = _ref2.__focusBehavior,
      __focusBehavior = _ref2$__focusBehavior === void 0 ? 'default' : _ref2$__focusBehavior,
      autoFocus = _ref2.autoFocus,
      isDisabled = _ref2.isDisabled,
      onFocus = _ref2.onFocus,
      onBlur = _ref2.onBlur,
      _onChange = _ref2.onChange,
      onKeyUp = _ref2.onKeyUp,
      onKeyDown = _ref2.onKeyDown,
      onClick = _ref2.onClick,
      value = _ref2.value,
      label = _ref2.label,
      isLabelHidden = _ref2.isLabelHidden,
      isRequired = _ref2.isRequired,
      error = _ref2.error,
      errorElementId = _ref2.errorElementId,
      type = _ref2.type,
      title = _ref2.title,
      description = _ref2.description,
      hint = _ref2.hint,
      placeholder = _ref2.placeholder,
      autoComplete = _ref2.autoComplete,
      name = _ref2.name,
      spellCheck = _ref2.spellCheck,
      readOnly = _ref2.readOnly,
      renderBeforeContent = _ref2.renderBeforeContent,
      renderAfterContent = _ref2.renderAfterContent,
      hasConnectedContentAbove = _ref2.hasConnectedContentAbove,
      hasConnectedContentBelow = _ref2.hasConnectedContentBelow,
      renderConnectedBefore = _ref2.renderConnectedBefore,
      renderConnectedAfter = _ref2.renderConnectedAfter,
      isMultiline = _ref2.isMultiline,
      maxLength = _ref2.maxLength,
      minLength = _ref2.minLength,
      pattern = _ref2.pattern,
      id = _ref2.id,
      accessibilityDescribedBy = _ref2.accessibilityDescribedBy,
      hasDefaultRenderContent = _ref2.hasDefaultRenderContent,
      inputMode = _ref2.inputMode,
      interfaceRef = _ref2.interfaceRef,
      role = _ref2.role,
      forceOnChange = _ref2.forceOnChange,
      props = _objectWithoutProperties(_ref2, ["isResizeable", "size", "isRounded", "__focusBehavior", "autoFocus", "isDisabled", "onFocus", "onBlur", "onChange", "onKeyUp", "onKeyDown", "onClick", "value", "label", "isLabelHidden", "isRequired", "error", "errorElementId", "type", "title", "description", "hint", "placeholder", "autoComplete", "name", "spellCheck", "readOnly", "renderBeforeContent", "renderAfterContent", "hasConnectedContentAbove", "hasConnectedContentBelow", "renderConnectedBefore", "renderConnectedAfter", "isMultiline", "maxLength", "minLength", "pattern", "id", "accessibilityDescribedBy", "hasDefaultRenderContent", "inputMode", "interfaceRef", "role", "forceOnChange"]);

  if (!readOnly && typeof _onChange === 'undefined') {
    console.error('WARNING: you have not provided an onChange callback for your Prism Field component. onChange should only be omitted when "readOnly" is set to true.');
  }

  var inputRef = useRef(null); // Using to handle provide the ability to focus the input field
  // while keeping the internals of the component private.

  useImperativeHandle(interfaceRef, function () {
    return {
      focus: function focus() {
        if (inputRef && inputRef.current && typeof inputRef.current.focus === 'function') {
          inputRef.current.focus();
        }
      }
    };
  });
  var theme = React__default.useContext(ThemingContext);
  var inputTextStyles = getThemeFieldTextStyles({
    theme: theme,
    size: size,
    styleType: 'Input'
  });
  var propsToForward = getPropsToForward({
    props: props,
    prefixesToForward: ['data-', 'aria-']
  });
  var isTextArea = isMultiline && type === 'text';
  /**
   * The below useEffect block attempts to fix a long standing issue with browser's autofill
   * features not always triggering functions passed into an input's onChange handler.
   * While the below implementation is browser agnostic and would fix any onChange triggers problems, it was put in specifically to handle
   * Google Chrome on iOS. The below is not guaranteed to keep working as the browser autofill issues changes quickly!
   * https://github.com/facebook/react/issues/1159
   */

  useEffect(function () {
    var input = inputRef.current;

    var handler = function handler(event) {
      // This is generally true in situations where the value of a raw DOM input has been updated,
      // but onChange was not triggered.
      if (value !== (input === null || input === void 0 ? void 0 : input.value)) {
        _onChange(event.target.value, // this onChange handler expects a React event, casting here for now in lieu of necessitating additional refactoring
        event);
      }
    };

    if (forceOnChange && input) {
      input.addEventListener('change', handler);
    }

    return function () {
      if (forceOnChange) {
        input === null || input === void 0 ? void 0 : input.removeEventListener('change', handler);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return /*#__PURE__*/React__default.createElement(WrappedFieldWrapper, {
    label: label,
    description: description,
    hint: hint,
    error: error,
    errorElementId: errorElementId,
    isDisabled: isDisabled,
    isLabelHidden: isLabelHidden,
    renderBeforeContent: renderBeforeContent,
    renderAfterContent: renderAfterContent,
    hasConnectedContentAbove: hasConnectedContentAbove,
    hasConnectedContentBelow: hasConnectedContentBelow,
    renderConnectedBefore: renderConnectedBefore,
    renderConnectedAfter: renderConnectedAfter,
    id: id,
    accessibilityDescribedBy: accessibilityDescribedBy,
    size: size,
    isRounded: isRounded,
    isAdjustableHeight: isTextArea,
    hasDefaultRenderContent: hasDefaultRenderContent,
    onClick: onClick,
    inputRef: inputRef,
    __focusBehavior: __focusBehavior
  }, /*#__PURE__*/React__default.createElement(InputContent, _extends({}, propsToForward, {
    as: isTextArea ? 'textarea' : 'input',
    title: title,
    value: value,
    onChange: function onChange(event) {
      return _onChange(event.target.value, event);
    },
    onKeyUp: onKeyUp,
    onKeyDown: onKeyDown,
    type: type,
    role: role // Only one of `<label>` and `aria-label` should be implemented
    // Using two or more together (e.g., a hidden <label> and a
    // duplicate title attribute) can cause information to be
    // repeated by a screen reader.
    // http://webaim.org/techniques/forms/advanced#hiddenlabel
    ,
    "aria-label": isLabelHidden ? label : null,
    required: isRequired,
    readOnly: readOnly,
    autoComplete: autoComplete,
    spellCheck: spellCheck,
    disabled: isDisabled,
    autoFocus: autoFocus,
    onFocus: onFocus,
    onBlur: onBlur,
    placeholder: placeholder,
    numberOfLines: numberOfLines({
      isMultiline: isMultiline,
      type: type
    }),
    maxLength: maxLength,
    minLength: minLength,
    isResizeable: isResizeable,
    isMultiline: isMultiline,
    inputSize: size,
    pattern: pattern,
    mobileInputTextStyles: inputTextStyles.mobile,
    desktopInputTextStyles: inputTextStyles.desktop,
    inputMode: inputMode,
    name: name
  })));
};

RawTextField.Sizes = RawTextFieldSize;
var RawTextDefaultProps = {
  isResizeable: true,
  size: RawTextFieldSize.Medium,
  isRounded: false,
  __focusBehavior: 'default'
};

export { RawTextDefaultProps, RawTextFieldSize, RawTextField as default };
